// frontend/src/features/ups/OrganizedDriverList.js
// Renders UPS Active (“Organized”) drivers using Phase 5 assignments read model,
// while keeping existing actions (details/reserve/ban) intact.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye, Ban as BannedIcon } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';

// ✅ Phase 7: site context (new)
import { selectSelectedSiteId } from '../sites/sitesSlice';

// Phase 5: assignments-based read model
import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';

import { upsertDriver } from '../drivers/driverSlice';

import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import DriverDetailsModal from './modals/DriverDetailsModal';
import { reserveDriver, banDriver } from '../../services/upsDriverService';


export default function OrganizedDriverList() {
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q } = useSelector(selectFilters);

  // ✅ Phase 7: selected site id from global SiteSelector
  const selectedSiteId = useSelector(selectSelectedSiteId);

  // Phase 5: select assignment rows for the Active/Organized tab
  const selectActiveAssignments = useMemo(() => makeSelectAssignmentsForTab('active'), []);
  const rows = useSelector(selectActiveAssignments);

  // ✅ Phase 7: load assignments whenever site/filters change
  useEffect(() => {
    if (!selectedSiteId) return;
    dispatch(loadSiteAssignments({ siteId: selectedSiteId, statuses: ['active'] }));
  }, [dispatch, agencyId, q, selectedSiteId]);

  // Focus-on-arrival via ?focus=<id> (driver id)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Reserve: reserve then reload assignments for current site
  const handleReserve = async (driverId) => {
    if (!selectedSiteId) return;
    try {
      await reserveDriver(driverId, selectedSiteId);
      dispatch(loadSiteAssignments({ siteId: selectedSiteId, statuses: ['active'] }));
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Reserve failed:', err);
    }
  };

  // Ban modal
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [banReason, setBanReason] = useState('');

  const openBanModal = (row) => {
    if (!isAdmin) return;

    dispatch(upsertDriver(row)); // ✅ keeps driverSlice consistent for anything modal-related

    setSelectedDriver(row);
    setBanReason('');
    setShowBanModal(true);
  };

  const handleBanConfirm = async () => {
    if (!selectedSiteId) return;
    if (!banReason.trim() || !isAdmin) return;

    try {
      if (!selectedSiteId) return;
      if (!selectedDriver?.id) return;

      const reason = banReason.trim();
      if (!reason) return;

      const banned = await banDriver(selectedDriver.id, reason);

      dispatch(upsertDriver(banned));
      dispatch(loadSiteAssignments({ siteId: selectedSiteId, statuses: ['active'] }));

      dispatch(bumpStatsRefreshKey()); 

      setShowBanModal(false);
      setSelectedDriver(null);
    } catch (err) {
      console.error('Ban failed:', err);
    }
  };

  // Driver details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);
  const openDetails = (row) => {
    dispatch(upsertDriver(row)); // ✅ prime redux so modal is live immediately
    setDetailsDriver(row);
    setShowDetails(true);
  };
  const closeDetails = () => {
    setShowDetails(false);
    setDetailsDriver(null);
  };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();
  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Organized Drivers</h3>
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name', 'Vehicle Reg.', 'Phone', 'Email', 'Agency', 'Submitted', 'Actions'].map(
                (col) => (
                  <th
                    key={col}
                    className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase"
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No active drivers{agencyId ? ` for ${scopeLabel}` : ''}
                  {hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : (
              (rows || []).filter((r) => !r?.is_banned).map((row) => (
                <tr
                  key={row.assignment_id ?? row.id}
                  data-row-id={row.id} // focus uses driver id
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span>{row.name}</span>

                      {row.is_banned && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                          {row.ban_scope === 'COMPANY' ? 'Company Banned' : 'Site Banned'}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.vehicle_registration_number || row.vrn || '—'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">{row.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{row.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.agency_name || row.agency?.name || '—'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.submission_date ? new Date(row.submission_date).toLocaleString() : '—'}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4">
                      {/* Details first (inspect before act) */}
                      <button
                        onClick={() => openDetails(row)}
                        aria-label="View driver details"
                        title="View details"
                        className="p-1 hover:bg-gray-100 rounded text-blue-600"
                      >
                        <Eye size={16} />
                      </button>

                      {/* Reserve: disable if banned */}
                      <button
                        onClick={() => handleReserve(row.id)}
                        disabled={!!row.is_banned}
                        aria-label="Reserve driver"
                        title={row.is_banned ? 'Driver is banned' : 'Reserve'}
                        className={
                          'p-1 rounded ' +
                          (row.is_banned
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'hover:bg-gray-100 text-yellow-600')
                        }
                      >
                        <UserCheck size={16} />
                      </button>

                      {/* Ban (admins only) */}
                      <button
                        onClick={() => openBanModal(row)}
                        aria-label="Ban driver"
                        title={isAdmin ? 'Ban' : 'Admins only'}
                        disabled={!isAdmin}
                        className={
                          'p-1 rounded ' +
                          (isAdmin
                            ? 'hover:bg-gray-100 text-red-600'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        }
                      >
                        <BannedIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowBanModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-md p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Ban {selectedDriver?.name}</h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                aria-label="Close ban modal"
              >
                &times;
              </button>
            </div>

            <textarea
              rows={4}
              placeholder="Enter ban reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBanModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleBanConfirm}
                disabled={!isAdmin}
                title={isAdmin ? 'Confirm Ban' : 'Admins only'}
                className={
                  'px-4 py-2 rounded ' +
                  (isAdmin
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                }
              >
                Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal */}
      {showDetails && detailsDriver && (
        <DriverDetailsModal driver={detailsDriver} onClose={closeDetails} />
      )}
    </>
  );
}
